import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Input,
} from "@salestrak/ui";

interface LinkPromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (url: string) => void;
}

/**
 * URL prompt for the editor's "Insert link" action. One dialog for every
 * breakpoint — `bottomStickOnMobile` (the DialogPopup default) covers the
 * mobile sheet presentation, so there is no mobile fork.
 */
export function LinkPromptDialog({
  open,
  onOpenChange,
  onSubmit,
}: LinkPromptDialogProps) {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValue("");
      // Deferred past the open transition so Base UI's initial-focus handling
      // doesn't race the imperative focus.
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const submit = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed) onSubmit(trimmed);
    onOpenChange(false);
  }, [value, onSubmit, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup size="sm">
        <DialogHeader>
          <DialogTitle>Insert link</DialogTitle>
        </DialogHeader>
        <Box
          as="form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <DialogPanel>
            <Input
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="https://example.com"
              // inputMode (not type="url"): the URL keyboard without native
              // form validation — scheme-less links are legal here.
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
            />
          </DialogPanel>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!value.trim()}>
              Insert
            </Button>
          </DialogFooter>
        </Box>
      </DialogPopup>
    </Dialog>
  );
}
